import { useEffect, useState } from 'react';
import { Activity, RefreshCw, Shield, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authHeaders } from '../../context/WorkspaceContext';
import { PLAN_ORDER, PLANS } from '../../plans';
import { Alert, Badge, Button, EmptyState, MetricCard, PageHeader, SectionHeader, Skeleton } from '../../components/ui';
import { useToast } from '../../components/ui/ToastProvider';
import DataTable from '../../components/ui/DataTable';
import './dashboard.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

export default function AdminPage() {
  const { user } = useAuth();
  const { notify } = useToast();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState({ scans: [], keywords: [], prospects: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [usersResponse, activityResponse] = await Promise.all([
        fetch(`${API}/admin/users`, { headers: authHeaders() }),
        fetch(`${API}/admin/activity`, { headers: authHeaders() }),
      ]);
      const usersData = await usersResponse.json();
      if (!usersResponse.ok) throw new Error(usersData.error || 'Could not load accounts.');
      setUsers(usersData.users);
      setStats(usersData.stats);
      if (activityResponse.ok) setActivity(await activityResponse.json());
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.role === 'admin') load(); else setLoading(false); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (path, body, message) => {
    try {
      const response = await fetch(`${API}${path}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Update failed.');
      await load();
      notify(message);
    } catch (requestError) {
      notify(requestError.message, 'error');
    }
  };

  const changeRole = async (userId, role) => { setUpdatingId(userId); await patch(`/admin/users/${userId}/role`, { role }, 'Role updated.'); setUpdatingId(null); };
  const changePlan = async (userId, plan) => { setUpdatingId(userId); await patch(`/admin/users/${userId}/plan`, { plan }, 'Plan updated.'); setUpdatingId(null); };

  if (user?.role !== 'admin') {
    return (
      <div className="dt-stack-5">
        <PageHeader title="Accounts" description="Administrator access is required for this page." />
        <div className="dt-card">
          <EmptyState
            icon={Shield}
            title="You do not have access to this page"
            description="Account administration is limited to Data Tallied administrators."
            actions={<Button to="/dashboard">Back to overview</Button>}
          />
        </div>
      </div>
    );
  }

  if (loading) return <div className="dt-stack-4"><Skeleton height={80} /><Skeleton height={110} /><Skeleton height={320} /></div>;

  return (
    <div className="dt-stack-5">
      <PageHeader
        title="Accounts"
        description="Monitor account growth and manage access across Data Tallied."
        actions={<Button variant="secondary" onClick={load}><RefreshCw size={15} />Refresh</Button>}
      />

      {error && <Alert tone="danger">{error}</Alert>}

      <div className="dt-metric-grid">
        <MetricCard label="Total users" icon={Users} value={stats?.totalUsers ?? '—'} footer="Registered accounts" />
        <MetricCard label="Administrators" icon={Shield} value={stats?.admins ?? '—'} footer="With full access" />
        <MetricCard label="Joined this week" icon={Activity} value={stats?.joinedThisWeek ?? '—'} footer="New signups" />
        <MetricCard label="Visibility scans" icon={Activity} value={activity.counts?.scans ?? 0} footer="Across all accounts" />
      </div>

      <section className="dt-stack-3" aria-label="User directory">
        <SectionHeader title="User management" description="Change a plan or role. Both are enforced by the API." />
        <DataTable
          rows={users}
          searchKeys={['name', 'email']}
          searchPlaceholder="Search users"
          initialSort={{ id: 'created', direction: 'desc' }}
          caption="Registered accounts"
          empty={<EmptyState title="No accounts found" description="No accounts match that search." />}
          columns={[
            { id: 'name', header: 'User', primary: true, sortValue: row => row.name.toLowerCase(), render: row => (
              <span style={{ display: 'grid' }}><strong>{row.name}</strong><small className="dt-support">{row.email}</small></span>
            ) },
            { id: 'plan', header: 'Plan', render: row => (
              <select className="dt-select" style={{ width: 150 }} value={row.plan || 'launch'} disabled={updatingId === row.id} onChange={event => changePlan(row.id, event.target.value)} aria-label={`Plan for ${row.name}`}>
                {PLAN_ORDER.map(plan => <option value={plan} key={plan}>{PLANS[plan].name}</option>)}
              </select>
            ) },
            { id: 'role', header: 'Role', sortValue: row => row.role, render: row => <Badge tone={row.role === 'admin' ? 'info' : 'neutral'}>{row.role}</Badge> },
            { id: 'created', header: 'Joined', sortValue: row => new Date(row.created_at).getTime(), render: row => new Date(row.created_at).toLocaleDateString() },
            { id: 'actions', header: '', align: 'right', render: row => row.id === user.id
              ? <span className="dt-support">Current account</span>
              : <Button variant="secondary" size="sm" disabled={updatingId === row.id} onClick={() => changeRole(row.id, row.role === 'admin' ? 'user' : 'admin')}>{row.role === 'admin' ? 'Make user' : 'Make admin'}</Button> },
          ]}
          renderCard={row => (
            <>
              <strong className="dt-card-title">{row.name}</strong>
              <dl>
                <dt>Email</dt><dd>{row.email}</dd>
                <dt>Plan</dt><dd>{PLANS[row.plan || 'launch'].name}</dd>
                <dt>Role</dt><dd>{row.role}</dd>
              </dl>
            </>
          )}
        />
      </section>

      <section className="dt-stack-3" aria-label="Recent scan activity">
        <SectionHeader title="Recent visibility scans" description="Scan activity across every account." />
        <DataTable
          rows={activity.scans || []}
          searchKeys={['business', 'location']}
          searchPlaceholder="Search scans"
          pageSize={8}
          caption="Visibility scans across accounts"
          empty={<EmptyState title="No scans yet" description="Scans run by any account will appear here." />}
          columns={[
            { id: 'business', header: 'Business', primary: true, render: row => row.business || '—' },
            { id: 'owner', header: 'Account', render: row => row.user_name || row.email || 'Public visitor' },
            { id: 'location', header: 'Market', render: row => row.location || '—' },
            { id: 'score', header: 'Score', sortValue: row => Number(row.score), render: row => <Badge tone={row.score >= 70 ? 'success' : row.score >= 40 ? 'warning' : 'danger'}>{row.score}</Badge> },
          ]}
          renderCard={row => (
            <>
              <strong className="dt-card-title">{row.business || 'Scan'}</strong>
              <dl>
                <dt>Account</dt><dd>{row.user_name || row.email || 'Public visitor'}</dd>
                <dt>Score</dt><dd>{row.score}</dd>
              </dl>
            </>
          )}
        />
      </section>
    </div>
  );
}
